import { useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import {
    IntegrationsProvider,
    type NavigationScreenIntegrationsState,
} from '@gitbook/app-navigation';
import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    type PickerItemData,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonSkeleton,
    Divider,
    Page,
    PageFooter,
    PageMain,
    PageSection,
    PageSectionHeading,
    PageSectionNav,
    PageSectionWithNav,
    PageTabs,
    PageWallpaper,
    Pagination,
    Tab,
    TabSkeleton,
    Tabs,
    TabsHeading,
    TabsHeadingSkeleton,
    TextSkeleton,
    useScreenType,
} from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { useHeadTitle } from '@gitbook/util-react-head';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    FeaturedIntegrations,
    FeaturedIntegrationsSkeleton,
    INTEGRATIONS_CATEGORY_PROPS,
    INTEGRATIONS_PER_PAGE,
    INTEGRATIONS_PER_SECTION,
    useFeaturedIntegrations,
} from '../../Components';
import {
    NavigationScreenWithOrganization,
    useNavigation,
    useNavigationState,
    useSetAnalyticsScreen,
} from '../../navigation';
import { apiQuery, apiResource, useSuspensePaginatedAPIQuery } from '../../state';
import introBgGradientDark from './assets/integrations-intro-bg-dark.raw.svg';
import introBgGradientLight from './assets/integrations-intro-bg-light.raw.svg';
import {
    BuildYourOwnIntegration,
    IntegrationCategoryPicker,
    IntegrationsEmptyState,
    IntegrationsList,
    IntegrationsListSkeleton,
    IntegrationsPageIntro,
    IntegrationsPageIntroSkeleton,
} from './components';
import { useIntegrationsScreen } from './hooks';

/**
 * Screen for browsing integrations.
 */
export function IntegrationsScreen(props: {
    screen: NavigationScreenIntegrationsState;
}): React.ReactElement {
    return (
        <ErrorBoundary fallback={<IntegrationsEmptyState kind="error" />}>
            <React.Suspense fallback={<IntegrationsScreenSkeleton />}>
                <NavigationScreenWithOrganization screen={props.screen}>
                    {() => <IntegrationsScreenWithOrganization />}
                </NavigationScreenWithOrganization>
            </React.Suspense>

            <link rel="preload" as="image" href={introBgGradientLight} />
            <link rel="preload" as="image" href={introBgGradientDark} />
        </ErrorBoundary>
    );
}

/**
 * Lazy-loaded content of the integrations screen.
 */
function IntegrationsScreenWithOrganization(): React.ReactElement {
    const screen = useIntegrationsScreen();
    const { search, category, organization: organizationId, provider } = screen;

    useHeadTitle('Integrations');

    const [{ data: organization }, { data: integrations }, { data: installedIntegrations }] =
        useSuspenseQueries({
            queries: [
                apiQuery('getOrganizationById', { organizationId }),
                apiQuery(
                    'listOrganizationIntegrations',
                    { organizationId, limit: INTEGRATIONS_PER_PAGE },
                    { refetchOnMount: false }
                ),
                apiQuery('listOrganizationIntegrationsStatus', {
                    organizationId,
                    target: 'organization',
                    limit: 100,
                }),
            ],
        });

    assertIsDefined(installedIntegrations);

    const featuredIntegrationsToDisplay = useFeaturedIntegrations({
        integrations: integrations?.items,
        installedIntegrations:
            installedIntegrations?.items.map((installation) => installation.integration.name) || [],
        useCase: organization.useCase,
        limit: 2,
    });

    useSetAnalyticsScreen(
        {
            screen: 'integrations_list',
            organization: organization.id,
        },
        [organization]
    );

    return (
        <Page>
            <PageWallpaper
                imageLight={introBgGradientLight}
                imageDark={introBgGradientDark}
                className="bg-auto bg-top"
            >
                <div className="flex flex-col gap-16">
                    <IntegrationsPageIntro />

                    <PageMain suspense={false}>
                        {featuredIntegrationsToDisplay.length === 0 || (!search && !category) ? (
                            <PageSection>
                                <FeaturedIntegrations
                                    title={
                                        <PageSectionHeading>
                                            Featured for your team
                                        </PageSectionHeading>
                                    }
                                    kind="enhanced"
                                    integrations={featuredIntegrationsToDisplay}
                                    organizationId={organization.id}
                                    className="grid grid-cols-2 gap-10 max-sm:grid-cols-1"
                                />
                            </PageSection>
                        ) : null}

                        <PageSectionWithNav>
                            <PageSectionNav>
                                <IntegrationsListNav />
                            </PageSectionNav>

                            <React.Suspense
                                fallback={
                                    <IntegrationsListByCategorySkeleton limit={search ? 1 : 2} />
                                }
                            >
                                <Integrations
                                    search={search}
                                    category={category}
                                    organization={organization.id}
                                    provider={provider}
                                    installedIntegrations={installedIntegrations.items}
                                />
                            </React.Suspense>
                        </PageSectionWithNav>
                    </PageMain>
                </div>
            </PageWallpaper>

            <PageFooter className="mt-auto">
                <Divider />
                <BuildYourOwnIntegration />
            </PageFooter>
        </Page>
    );
}

/**
 * Vertical tabs navigation to filter the integrations list.
 */
function IntegrationsListNav(): React.ReactElement {
    const { category: screenCategory } = useIntegrationsScreen();

    return (
        <>
            <PageTabs
                orientation="vertical"
                className="max-lg:hidden"
                aria-label="Integrations list category navigation"
            >
                <div className="flex items-center justify-between">
                    <TabsHeading>Categories</TabsHeading>
                    <IntegrationsProviderPicker className="max-lg:hidden" />
                </div>

                <Tabs orientation="vertical">
                    <Tab
                        label="All"
                        active={screenCategory === undefined}
                        href={{ current: { category: undefined } }}
                    />
                    {Object.values(api.IntegrationCategory).map((category) => (
                        <Tab
                            key={category}
                            label={INTEGRATIONS_CATEGORY_PROPS[category].label}
                            active={screenCategory === category}
                            href={{ current: { category } }}
                        />
                    ))}
                </Tabs>
            </PageTabs>

            <IntegrationCategoryPicker
                className="lg:hidden"
                aria-label="Integrations list category navigation"
            />

            <IntegrationsProviderPicker className="lg:hidden" />
        </>
    );
}

/**
 * Switch to fetch integrations from marketplace or integrations owned by the current org.
 */
function IntegrationsProviderPicker(props: { className?: string }): React.ReactElement {
    const { isMobileOrTablet } = useScreenType();
    const { provider } = useIntegrationsScreen();
    const { navigate } = useNavigation();

    return (
        <PaletteButton
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'filter',
                            title: 'Filter by',
                            commands: integrationsProviderOptions.map((option) => ({
                                key: option.provider,
                                title: option.label,
                                trigger: () => {
                                    navigate({ current: { provider: option.value } });
                                },
                                type: PaletteCommandType.RADIO,
                                selected: provider === option.value,
                            })),
                        },
                    ],
                })
            }
            iconOnly
            leadingIcon={Icon.Filter}
            label="Select integration provider"
            kind={isMobileOrTablet ? 'secondary' : 'blank'}
            className={props.className}
        />
    );
}
const integrationsProviderOptions: Array<
    PickerItemData<IntegrationsProvider | undefined> & { provider: IntegrationsProvider }
> = [
    {
        value: undefined,
        provider: IntegrationsProvider.Marketplace,
        label: 'Marketplace integrations',
    },
    {
        value: IntegrationsProvider.Organization,
        provider: IntegrationsProvider.Organization,
        label: 'My integrations',
    },
];

/**
 * Section of the integrations screen displaying the list of integrations.
 */
export function Integrations(props: {
    organization: string;
    installedIntegrations: api.InstalledIntegration[];
    target?: api.IntegrationTarget;
    search?: string;
    category?: api.IntegrationCategory;
    provider?: IntegrationsProvider;
}): React.ReactElement {
    const { search, category, organization, installedIntegrations, provider, target } = props;

    const { data, previous, next } = useSuspensePaginatedAPIQuery(
        apiResource(
            'listOrganizationIntegrations',
            {
                organizationId: organization,
                limit: INTEGRATIONS_PER_PAGE,
                search,
                category,
                target,
                owner: provider === IntegrationsProvider.Organization ? organization : undefined,
            },
            // Refactor: handle the error case in a react boundary
            { nullOnError: true }
        )
    );

    if (!data || data.items.length === 0) {
        return (
            <IntegrationsEmptyState
                kind={provider === IntegrationsProvider.Organization ? 'no-custom' : 'not-found'}
            />
        );
    }

    const integrations = data.items;

    if (!category) {
        return (
            <AllIntegrationsByCategory
                integrations={integrations}
                installedIntegrations={installedIntegrations}
                organizationId={organization}
            />
        );
    }

    return (
        <div className="flex w-full flex-col gap-4">
            <IntegrationsListByCategorySection
                categoryKey={category}
                screenCategory={category}
                integrations={integrations}
                organizationId={organization}
                installedIntegrations={installedIntegrations}
            />

            <Pagination previous={previous} next={next} />
        </div>
    );
}

const AllIntegrationsByCategory = React.memo(function AllIntegrationsByCategory(props: {
    integrations: api.Integration[];
    installedIntegrations: api.InstalledIntegration[];
    organizationId: string;
}) {
    const { integrations, installedIntegrations, organizationId } = props;

    // Reset integrations by categories map when data changes to avoid duplicates
    const integrationsCategoryPropsCopy = Object.keys(INTEGRATIONS_CATEGORY_PROPS).reduce(
        (acc, categoryKey) => {
            const categoryProps = INTEGRATIONS_CATEGORY_PROPS[categoryKey];
            acc[categoryKey] = {
                ...categoryProps,
                integrations: new Map(),
            };
            return acc;
        },
        {}
    );

    for (const integration of integrations) {
        integration.categories.forEach((category) => {
            const categoryProps = integrationsCategoryPropsCopy[category];

            if (!categoryProps.integrations) {
                categoryProps.integrations = new Map();
            }
            if (!categoryProps.integrations.has(integration.name)) {
                categoryProps.integrations.set(integration.name, integration);
            }
        });
    }

    for (const categoryKey of Object.keys(integrationsCategoryPropsCopy)) {
        if (!integrationsCategoryPropsCopy[categoryKey].integrations.size) {
            delete integrationsCategoryPropsCopy[categoryKey];
        }
    }

    return (
        <ul className="flex w-full flex-col gap-10">
            {Object.values(integrationsCategoryPropsCopy).map(
                ({ value: categoryKey, integrations }) => {
                    if (!integrations || integrations?.size === 0) {
                        return null;
                    }
                    return (
                        <li key={categoryKey}>
                            <IntegrationsListByCategorySection
                                categoryKey={categoryKey}
                                integrations={Array.from(integrations.values())}
                                organizationId={organizationId}
                                installedIntegrations={installedIntegrations}
                            />
                        </li>
                    );
                }
            )}
        </ul>
    );
});

/**
 * A section with a title for a category of integrations.
 */
function IntegrationsListByCategorySection(props: {
    installedIntegrations: api.InstalledIntegration[];
    /** The category key needed for the section title. */
    categoryKey: api.IntegrationCategory;
    /** The category URL param needed to show/hide the "See all" button. */
    screenCategory?: api.IntegrationCategory;
    integrations: api.Integration[];
    organizationId: string;
}): React.ReactElement | null {
    const { categoryKey, screenCategory, integrations, installedIntegrations, organizationId } =
        props;
    const { screen } = useNavigationState();

    const isIntegrationsModal =
        ['space', 'site'].includes(screen.type) &&
        'sideSheet' in screen &&
        screen.sideSheet?.type === 'integrations';

    return (
        <PageSection key={categoryKey}>
            <div className="flex items-center justify-between">
                <PageSectionHeading>
                    {INTEGRATIONS_CATEGORY_PROPS[categoryKey].label}
                </PageSectionHeading>
                {/* Don't show 'See all' button on a category page */}
                {!screenCategory && integrations.length > INTEGRATIONS_PER_SECTION ? (
                    <Button
                        label="See all"
                        kind="blank"
                        href={{
                            current: {
                                ...(isIntegrationsModal
                                    ? {
                                          sideSheet: {
                                              type: 'integrations',
                                              view: {
                                                  type: 'category',
                                                  category: categoryKey,
                                                  search: '',
                                              },
                                          },
                                      }
                                    : {
                                          category: categoryKey,
                                          search: '',
                                      }),
                            },
                        }}
                    />
                ) : null}
            </div>

            <IntegrationsList
                integrations={
                    !screenCategory ? integrations.slice(0, INTEGRATIONS_PER_SECTION) : integrations
                }
                installedIntegrations={installedIntegrations}
                organizationId={organizationId}
            />
        </PageSection>
    );
}

/** Skeletons for the screen loading state */
function IntegrationsScreenSkeleton(): React.ReactElement {
    return (
        <Page>
            <div className="flex flex-col gap-[4.5rem]">
                <IntegrationsPageIntroSkeleton />

                <PageMain>
                    <PageSection>
                        <TextSkeleton className="text-ui-heading-medium">
                            Featured for your team
                        </TextSkeleton>

                        <FeaturedIntegrationsSkeleton
                            className="grid grid-cols-2 gap-10 max-sm:grid-cols-1"
                            count={2}
                        />
                    </PageSection>

                    <PageSectionWithNav>
                        <PageSectionNav>
                            <IntegrationsListNavSkeleton />
                        </PageSectionNav>

                        <IntegrationsListByCategorySkeleton />
                    </PageSectionWithNav>
                </PageMain>
            </div>
        </Page>
    );
}

function IntegrationsListNavSkeleton(): React.ReactElement {
    return (
        <>
            <PageTabs
                orientation="vertical"
                className="max-lg:hidden"
                aria-label="Integrations list category navigation"
            >
                <div className="flex items-center justify-between">
                    <TabsHeadingSkeleton>Categories</TabsHeadingSkeleton>
                    <ButtonSkeleton iconOnly label="Select integration provider" />
                </div>

                <Tabs orientation="vertical">
                    <TabSkeleton label="All" />
                    {Object.values(api.IntegrationCategory).map((category) => (
                        <TabSkeleton
                            key={category}
                            label={INTEGRATIONS_CATEGORY_PROPS[category].label}
                        />
                    ))}
                </Tabs>
            </PageTabs>

            <ButtonSkeleton className="lg:hidden" label="Integrations list category navigation" />

            <ButtonSkeleton iconOnly label="Select integration provider" className="lg:hidden" />
        </>
    );
}

function IntegrationsListByCategorySkeleton(props: { limit?: number }): React.ReactElement {
    const { limit = 6 } = props;
    return (
        <PageSection>
            <div className="flex items-center justify-between">
                <TextSkeleton className="text-ui-heading-medium">Category</TextSkeleton>

                <ButtonSkeleton label="See all" />
            </div>

            <IntegrationsListSkeleton limit={limit} />
        </PageSection>
    );
}
